import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CalendarDays, Type, Image as ImageIcon, Upload, Link2, Users, Globe2, UserRound, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { programs } from "../../mock/mock";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const AUD_LABEL = { todos: "General (todos)", programa: "Por programa", usuarios: "Usuarios específicos" };
const todayStr = () => new Date().toISOString().slice(0, 10);

const TIMEZONES = [
  ["America/Mexico_City", "Ciudad de México (CDMX)"],
  ["America/Tijuana", "México · Noroeste (Tijuana)"],
  ["America/Cancun", "México · Sureste (Cancún)"],
  ["America/Guatemala", "Centroamérica (Guatemala/El Salvador)"],
  ["America/Costa_Rica", "Costa Rica"],
  ["America/Panama", "Panamá"],
  ["America/Bogota", "Colombia (Bogotá)"],
  ["America/Lima", "Perú (Lima)"],
  ["America/Guayaquil", "Ecuador (Guayaquil)"],
  ["America/Caracas", "Venezuela (Caracas)"],
  ["America/La_Paz", "Bolivia (La Paz)"],
  ["America/Santiago", "Chile (Santiago)"],
  ["America/Argentina/Buenos_Aires", "Argentina (Buenos Aires)"],
  ["America/Montevideo", "Uruguay (Montevideo)"],
  ["America/Asuncion", "Paraguay (Asunción)"],
  ["America/Santo_Domingo", "Rep. Dominicana"],
  ["America/New_York", "EE. UU. Este (Nueva York/Miami)"],
  ["America/Chicago", "EE. UU. Centro (Chicago)"],
  ["America/Denver", "EE. UU. Montaña (Denver)"],
  ["America/Los_Angeles", "EE. UU. Pacífico (Los Ángeles)"],
  ["Europe/Madrid", "España (Madrid)"],
];
const TZ_LABEL = Object.fromEntries(TIMEZONES);

// Convierte una hora "de pared" (fecha+hora en tz) al horario equivalente en CDMX (para coordinar).
function tzOffsetMinutes(tz, at) {
  const dtf = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const p = dtf.formatToParts(at).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return (asUTC - at.getTime()) / 60000;
}
function equivEnCDMX(fecha, hora, tz) {
  if (!fecha || !hora || tz === "America/Mexico_City") return null;
  try {
    const [y, mo, d] = fecha.split("-").map(Number);
    const [h, mi] = hora.split(":").map(Number);
    let ts = Date.UTC(y, mo - 1, d, h, mi);
    for (let i = 0; i < 2; i++) ts = Date.UTC(y, mo - 1, d, h, mi) - tzOffsetMinutes(tz, new Date(ts)) * 60000;
    return new Intl.DateTimeFormat("es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit", weekday: "short", day: "2-digit", month: "short" }).format(new Date(ts));
  } catch { return null; }
}

const emptyForm = () => ({
  fecha: todayStr(), hora: "", zona_horaria: "America/Mexico_City", tipo: "texto", titulo: "", texto: "", imagen_url: "",
  audiencia_tipo: "todos", programas: [], grupos: [], usuarios: [], visible_invitados: false,
});

export default function ReflexionesAdmin() {
  const res = adminApi.reflexiones();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [toDelete, setToDelete] = useState(null);
  const [imgMode, setImgMode] = useState("upload");
  const [users, setUsers] = useState([]);
  const [uSearch, setUSearch] = useState("");

  const load = () => {
    setLoading(true);
    res.list().then(setItems).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    adminApi.listUsers().then((u) => setUsers(u.filter((x) => x.role !== "admin"))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(emptyForm()); setImgMode("upload"); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...emptyForm(), ...it, programas: it.programas || [], grupos: it.grupos || [], usuarios: it.usuarios || [] });
    setImgMode(it.imagen_url && it.imagen_url.startsWith("http") ? "url" : "upload");
    setOpen(true);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("La imagen debe pesar menos de 1.5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => set("imagen_url", reader.result);
    reader.readAsDataURL(f);
  };

  const toggleArr = (key, val) => {
    setForm((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const save = async () => {
    if (!form.fecha) { toast.error("Selecciona la fecha."); return; }
    if (form.tipo === "texto" && !form.texto.trim()) { toast.error("Escribe el texto de la reflexión."); return; }
    if (form.tipo === "imagen" && !form.imagen_url) { toast.error("Agrega una imagen (archivo o URL)."); return; }
    if (form.audiencia_tipo === "programa" && form.programas.length === 0) { toast.error("Selecciona al menos un programa."); return; }
    if (form.audiencia_tipo === "usuarios" && form.usuarios.length === 0) { toast.error("Selecciona al menos un usuario."); return; }
    try {
      if (editing) await res.update(editing.id, form);
      else await res.create(form);
      toast.success("Reflexión guardada");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try { await res.remove(toDelete.id); toast.success("Eliminada"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filteredUsers = users.filter((u) =>
    `${u.nombre} ${u.apellidos} ${u.email} ${u.programa}`.toLowerCase().includes(uSearch.toLowerCase())
  );

  const audText = (it) => {
    if (it.audiencia_tipo === "programa") { const g = (it.grupos || []); return `Programas: ${(it.programas || []).join(", ")}${g.length ? ` · Grupos: ${g.join(", ")}` : ""}`; }
    if (it.audiencia_tipo === "usuarios") return `${(it.usuarios || []).length} usuario(s)`;
    return "General (todos)";
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0A1C30]">Reflexiones Supera</h1>
          <p className="text-[#6B7688] mt-1">Programa mensajes de liderazgo diarios (hasta 365 días).</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-3 transition-colors">
          <Plus className="w-5 h-5" /> Programar reflexión
        </button>
      </div>

      <div className="mt-8 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 bg-[#FAFAFA]">
                {["Fecha", "Tipo", "Contenido", "Audiencia"].map((h) => (
                  <th key={h} className="px-5 py-3.5 text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">{h}</th>
                ))}
                <th className="px-5 py-3.5 text-right text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-[#6B7688]">Cargando...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-[#6B7688]">Aún no hay reflexiones programadas.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]">
                  <td className="px-5 py-4 text-sm font-bold text-[#0A1C30] whitespace-nowrap">
                    {it.fecha}
                    {it.hora && (
                      <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#8E44AD]">
                        <Clock className="w-3 h-3" /> {it.hora} · {(TZ_LABEL[it.zona_horaria] || it.zona_horaria || "CDMX").split(" (")[0].split(" ·")[0]}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-[#2A3446]">
                      {it.tipo === "imagen" ? <ImageIcon className="w-4 h-4" /> : <Type className="w-4 h-4" />}
                      {it.tipo === "imagen" ? "Imagen" : "Texto"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#2A3446] max-w-xs truncate">{it.titulo || it.texto || "(imagen)"}</td>
                  <td className="px-5 py-4 text-sm text-[#6B7688]">{audText(it)}</td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="w-9 h-9 rounded-lg hover:bg-[#EDF0F4] text-[#3B7DF6] inline-flex items-center justify-center transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setToDelete(it)} className="w-9 h-9 rounded-lg hover:bg-[#FCE4E4] text-[#E23B3B] inline-flex items-center justify-center transition-colors ml-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">{editing ? "Editar" : "Programar"} reflexión</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Fecha */}
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Fecha de la reflexión</Label>
              <div className="relative">
                <CalendarDays className="w-4 h-4 text-[#6B7688] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input type="date" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} className="pl-9" />
              </div>
            </div>

            {/* Horario de publicación */}
            <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-3">
              <Label className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#8E44AD]" /> Horario de aparición (opcional)</Label>
              <p className="text-xs text-[#6B7688] -mt-1">Elige la zona horaria de referencia y la hora en que la reflexión debe aparecer ese día. Si lo dejas vacío, se mostrará todo el día.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold text-[#6B7688] mb-1 block">Hora (formato 24h)</Label>
                  <Input data-testid="reflexion-hora" type="time" value={form.hora} onChange={(e) => set("hora", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#6B7688] mb-1 block">Zona horaria (país)</Label>
                  <Select value={form.zona_horaria} onValueChange={(v) => set("zona_horaria", v)}>
                    <SelectTrigger data-testid="reflexion-tz"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {TIMEZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.hora && (
                <div className="text-xs font-semibold text-[#0A1C30] bg-white rounded-lg px-3 py-2 ring-1 ring-black/5" data-testid="reflexion-tz-hint">
                  Aparecerá a las <span className="font-extrabold">{form.hora}</span> ({TZ_LABEL[form.zona_horaria] || form.zona_horaria}).
                  {equivEnCDMX(form.fecha, form.hora, form.zona_horaria) && (
                    <span className="block text-[#8E44AD] mt-0.5">Equivale en CDMX a: {equivEnCDMX(form.fecha, form.hora, form.zona_horaria)}.</span>
                  )}
                </div>
              )}
            </div>

            {/* Tipo */}
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Tipo de contenido</Label>
              <div className="flex gap-2">
                {[["texto", "Texto", Type], ["imagen", "Imagen", ImageIcon]].map(([val, lbl, Ic]) => (
                  <button key={val} onClick={() => set("tipo", val)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold text-sm transition-all ${form.tipo === val ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#EDF0F4]"}`}>
                    <Ic className="w-4 h-4" /> {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Titulo */}
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Título (opcional)</Label>
              <Input value={form.titulo} onChange={(e) => set("titulo", e.target.value)} placeholder="Ej. La actitud del líder" />
            </div>

            {/* Content by type */}
            {form.tipo === "texto" ? (
              <div>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Mensaje de la reflexión</Label>
                <Textarea value={form.texto} onChange={(e) => set("texto", e.target.value)} className="min-h-[130px]" placeholder="Escribe el mensaje de liderazgo del día..." />
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-3">
                  {[["upload", "Subir archivo", Upload], ["url", "Desde URL", Link2]].map(([val, lbl, Ic]) => (
                    <button key={val} onClick={() => setImgMode(val)} className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-bold transition-colors ${imgMode === val ? "bg-[var(--gold)] text-[#0A1C30]" : "bg-[#F4F6F9] text-[#6B7688] hover:bg-[#EDF0F4]"}`}>
                      <Ic className="w-4 h-4" /> {lbl}
                    </button>
                  ))}
                </div>
                {imgMode === "upload" ? (
                  <input type="file" accept="image/*" onChange={onFile} className="block w-full text-sm text-[#6B7688] file:mr-3 file:rounded-lg file:border-0 file:bg-navy file:text-white file:px-4 file:py-2 file:font-bold file:cursor-pointer" />
                ) : (
                  <Input value={form.imagen_url} onChange={(e) => set("imagen_url", e.target.value)} placeholder="https://..." />
                )}
                {form.imagen_url && (
                  <img src={form.imagen_url} alt="preview" className="mt-3 max-h-48 rounded-xl object-contain bg-[#F4F6F9] w-full" />
                )}
                <p className="text-xs text-[#9AA4B4] mt-2">Archivos hasta 1.5 MB.</p>
              </div>
            )}

            {/* Audiencia */}
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">¿A quién se envía?</Label>
              <div className="grid grid-cols-3 gap-2">
                {[["todos", "General", Globe2], ["programa", "Por programa", Users], ["usuarios", "Usuarios", UserRound]].map(([val, lbl, Ic]) => (
                  <button key={val} onClick={() => set("audiencia_tipo", val)} className={`inline-flex flex-col items-center gap-1 rounded-xl px-3 py-3 text-xs font-bold transition-all ${form.audiencia_tipo === val ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#EDF0F4]"}`}>
                    <Ic className="w-4 h-4" /> {lbl}
                  </button>
                ))}
              </div>

              {form.audiencia_tipo === "programa" && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {programs.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.programas.includes(p.name)} onChange={() => toggleArr("programas", p.name)} className="accent-[#E9A400] w-4 h-4" />
                      <span className="text-[#2A3446]">{p.name}</span>
                    </label>
                  ))}
                </div>
              )}

              {form.audiencia_tipo === "programa" && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs font-bold text-[#0A1C30]">Grupos (opcional · vacío = todos los grupos)</Label>
                    {form.grupos.length > 0 && (
                      <button type="button" onClick={() => set("grupos", [])} className="text-[11px] font-bold text-[#8E44AD] hover:underline">Limpiar grupos</button>
                    )}
                  </div>
                  <div data-testid="reflexion-grupos" className="flex flex-wrap gap-1.5">
                    {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((g) => {
                      const on = form.grupos.includes(g);
                      return (
                        <button key={g} type="button" data-testid={`grupo-${g}`} onClick={() => toggleArr("grupos", g)}
                          className={`w-8 h-8 rounded-lg text-xs font-extrabold transition-colors ${on ? "bg-[var(--gold)] text-[#0A1C30]" : "bg-[#F4F6F9] text-[#6B7688] hover:bg-[#EDF0F4]"}`}>{g}</button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#9AA4B4] mt-1.5">{form.grupos.length === 0 ? "La reflexión llegará a todos los grupos del/los programa(s) seleccionado(s)." : `Solo grupos: ${form.grupos.join(", ")}`}</p>
                </div>
              )}

              {form.audiencia_tipo === "usuarios" && (
                <div className="mt-3">
                  <Input value={uSearch} onChange={(e) => setUSearch(e.target.value)} placeholder="Buscar usuario..." className="mb-2" />
                  <div className="max-h-52 overflow-y-auto supera-scroll rounded-xl border border-black/5 divide-y divide-black/5">
                    {filteredUsers.length === 0 && <p className="text-sm text-[#6B7688] p-3">No hay usuarios.</p>}
                    {filteredUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm hover:bg-[#FAFAFA]">
                        <input type="checkbox" checked={form.usuarios.includes(u.id)} onChange={() => toggleArr("usuarios", u.id)} className="accent-[#E9A400] w-4 h-4" />
                        <span className="text-[#2A3446]">{u.nombre} {u.apellidos} <span className="text-[#9AA4B4]">· {u.email}</span></span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-[#9AA4B4] mt-1">{form.usuarios.length} seleccionado(s)</p>
                </div>
              )}
            </div>

            {/* Visible para invitados */}
            <div className="flex items-center justify-between rounded-xl bg-[#F4F6F9] px-4 py-3">
              <div>
                <Label className="text-sm font-bold text-[#0A1C30]">Visible para invitados</Label>
                <p className="text-xs text-[#6B7688]">Permite que los usuarios invitados vean esta reflexión.</p>
              </div>
              <Switch checked={!!form.visible_invitados} onCheckedChange={(v) => set("visible_invitados", v)} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-xl px-5 py-2.5 font-bold text-[#6B7688] hover:bg-[#EDF0F4] transition-colors">Cancelar</button>
            <button onClick={save} className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-6 py-2.5 transition-colors">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta reflexión?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#E23B3B] hover:bg-[#c72f2f]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
